import { Link } from "react-router-dom";
import type { LocationData } from "../../../mocks/locationData";

interface LocationHeroProps {
  location: LocationData;
  onBookNow: () => void;
}

const floatingItems = [
  { icon: "ri-truck-line",         top: "15%",  left: "5%",   size: 52, bg: "#6CC412", delay: "0s",   cls: "anim-float" },
  { icon: "ri-delete-bin-7-line",  top: "10%",  right: "7%",  size: 44, bg: "#ffffff22", delay: "0.4s", cls: "anim-float-b" },
  { icon: "ri-sofa-line",          top: "65%",  left: "3%",   size: 48, bg: "#6CC412", delay: "0.8s", cls: "anim-float-c" },
  { icon: "ri-home-3-line",        top: "70%",  right: "5%",  size: 56, bg: "#ffffff22", delay: "1.1s", cls: "anim-float" },
  { icon: "ri-recycle-line",       top: "40%",  right: "2%",  size: 40, bg: "#6CC412", delay: "0.6s", cls: "anim-float-b" },
  { icon: "ri-fridge-line",        top: "30%",  left: "1%",   size: 38, bg: "#ffffff22", delay: "1.5s", cls: "anim-float-c" },
];

export default function LocationHero({ location, onBookNow }: LocationHeroProps) {
  return (
    <section
      className="relative overflow-hidden pt-36 pb-20"
      style={{ background: "#09245b" }}
    >
      {/* Dot grid */}
      <div
        className="absolute inset-0 opacity-10"
        style={{
          backgroundImage: "radial-gradient(circle, #6CC412 1px, transparent 1px)",
          backgroundSize: "30px 30px",
        }}
      />

      {/* Floating icons */}
      {floatingItems.map((fi, i) => (
        <div
          key={i}
          className={`absolute hidden lg:flex items-center justify-center rounded-2xl thick-border opacity-80 ${fi.cls}`}
          style={{
            top: fi.top,
            left: "left" in fi ? fi.left : undefined,
            right: "right" in fi ? fi.right : undefined,
            width: fi.size,
            height: fi.size,
            background: fi.bg,
            animationDelay: fi.delay,
            zIndex: 1,
          }}
        >
          <i className={`${fi.icon} text-white`} style={{ fontSize: fi.size * 0.4 }}></i>
        </div>
      ))}

      <div className="max-w-screen-xl mx-auto px-6 lg:px-10 relative z-10">
        {/* Breadcrumb */}
        <div className="flex items-center gap-2 mb-7 text-white/50 text-xs font-bold">
          <Link to="/" className="hover:text-white transition-colors cursor-pointer">Home</Link>
          <i className="ri-arrow-right-s-line"></i>
          <Link to="/locations" className="hover:text-white transition-colors cursor-pointer">Locations</Link>
          <i className="ri-arrow-right-s-line"></i>
          <span className="text-white/80">{location.city}</span>
        </div>

        <div className="max-w-3xl">
          {/* Badge */}
          <div
            className="inline-flex items-center gap-2 rounded-full px-4 py-1.5 mb-6 thick-border cartoon-shadow-sm anim-bounce-in"
            style={{ background: "#6CC412" }}
          >
            <i className="ri-map-pin-2-fill text-white text-xs"></i>
            <span className="text-white text-xs font-black uppercase tracking-widest">{location.city}, {location.county}</span>
          </div>

          {/* Headline */}
          <h1
            className="font-black text-white leading-[1.0] mb-5"
            style={{ fontSize: "clamp(2.8rem, 5.5vw, 4.8rem)" }}
          >
            {location.headline}
            <br />
            <span style={{ color: "#6CC412" }}>{location.city}</span>
          </h1>

          <p className="text-white/70 text-lg max-w-xl leading-relaxed font-semibold mb-8">
            {location.description}
          </p>

          {/* Stats row */}
          <div className="flex flex-wrap gap-5 mb-10">
            {location.stats.slice(0, 3).map((s) => (
              <div key={s.label} className="flex items-center gap-3">
                <span
                  className="w-10 h-10 flex items-center justify-center rounded-xl border-2 border-white/25 flex-shrink-0"
                  style={{ background: "rgba(255,255,255,0.1)" }}
                >
                  <i className={`${s.icon} text-cl-lime text-base`}></i>
                </span>
                <div>
                  <p className="text-white font-black text-sm leading-none">{s.value}</p>
                  <p className="text-white/50 text-xs font-semibold mt-0.5">{s.label}</p>
                </div>
              </div>
            ))}
          </div>

          {/* CTA buttons */}
          <div className="flex flex-col sm:flex-row gap-3 mb-10">
            <button
              onClick={onBookNow}
              className="bg-cl-green text-white font-black px-8 py-4 rounded-xl text-sm cartoon-btn-green whitespace-nowrap cursor-pointer"
            >
              <i className="ri-calendar-check-line mr-2"></i>
              Book in {location.city} — Fixed Price
            </button>
            <a
              href="tel:03335775445"
              className="flex items-center justify-center gap-2 font-black px-7 py-4 rounded-xl text-sm whitespace-nowrap cursor-pointer"
              style={{
                background: "rgba(255,255,255,0.1)",
                border: "2px solid rgba(255,255,255,0.25)",
                color: "#fff",
              }}
            >
              <i className="ri-phone-fill" style={{ color: "#6CC412" }}></i>
              03335 775445
            </a>
            <a
              href="https://wa.me/443335775445?text=Hi%20Cleario%21%20I%27d%20like%20a%20quote."
              target="_blank"
              rel="nofollow noopener noreferrer"
              className="flex items-center justify-center gap-2 bg-[#25D366] text-white font-black px-7 py-4 rounded-xl text-sm whitespace-nowrap cursor-pointer"
            >
              <i className="ri-whatsapp-line"></i>
              WhatsApp Quote
            </a>
          </div>

          {/* Trust chips */}
          <div className="flex flex-wrap gap-3">
            {[
              { icon: "ri-calendar-check-line", text: "Same-day slots" },
              { icon: "ri-shield-check-line",   text: "Licensed & insured" },
              { icon: "ri-price-tag-3-line",    text: "Fixed price — no surprises" },
              { icon: "ri-leaf-line",           text: "95%+ recycled" },
            ].map((t) => (
              <div
                key={t.text}
                className="flex items-center gap-1.5 rounded-full px-3 py-1.5"
                style={{ background: "rgba(255,255,255,0.08)", border: "1px solid rgba(255,255,255,0.18)" }}
              >
                <i className={`${t.icon} text-xs`} style={{ color: "#6CC412" }}></i>
                <span className="text-white/80 text-xs font-semibold">{t.text}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
